import i18n from "../i18n";
// wash.service.ts
import { AxiosResponse } from "axios";
import api from "./api";
import {
  IWashRequest,
  IGetWashRequestsResponse,
  IWashRequestResponse,
  IWashListApiResponse,
} from "../types/wash.types";

class WashService {
  /**
   * Get all wash requests (admin view)
   */
  public async getAllWashes(): Promise<IWashRequest[]> {
    try {
      const response: AxiosResponse<IGetWashRequestsResponse> =
        await api.get<IGetWashRequestsResponse>("/admins/washes");
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.fetchWashRequests"));
    }
  }

  /**
   * Get specific wash request by ID
   */
  public async getWashById(washId: string): Promise<IWashRequest> {
    try {
      const response: AxiosResponse<IWashRequestResponse> =
        await api.get<IWashRequestResponse>(`/admins/washes/${washId}`);
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.fetchWashRequestDetails"));
    }
  }

  /**
   * Get wash requests for a specific car wash
   */
  public async getCarWashWashes(carWashId: string): Promise<IWashRequest[]> {
    try {
      const response: AxiosResponse<IGetWashRequestsResponse> =
        await api.get<IGetWashRequestsResponse>(
          `/admins/carWashes/${carWashId}/washes`
        );
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.fetchCarWashWashes"));
    }
  }

  /**
   * Get all wash requests across every car wash owned by a specific owner
   */
  public async getOwnerWashes(ownerId: string): Promise<IWashRequest[]> {
    try {
      const response: AxiosResponse<IGetWashRequestsResponse> =
        await api.get<IGetWashRequestsResponse>(
          `/admins/owners/${ownerId}/washes`
        );
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.fetchOwnerWashingHistory"));
    }
  }

  /**
   * Get the full wash history for a single customer (admin per-customer tab).
   * No RM scoping — customers have no relationshipManager relationship.
   */
  public async getUserWashes(userId: string): Promise<IWashRequest[]> {
    try {
      const response: AxiosResponse<IWashListApiResponse> =
        await api.get<IWashListApiResponse>(`/admins/users/${userId}/washes`);
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.fetchUserWashes"));
    }
  }

  /**
   * Get the full wash history for a single employee (admin per-employee tab).
   * RM-scoped server-side via the employee's employer; super_admin unrestricted.
   */
  public async getEmployeeWashes(employeeId: string): Promise<IWashRequest[]> {
    try {
      const response: AxiosResponse<IWashListApiResponse> =
        await api.get<IWashListApiResponse>(
          `/admins/employees/${employeeId}/washes`
        );
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.fetchEmployeeWashes"));
    }
  }

  /**
   * Update wash request status (admin action)
   */
  public async updateWashStatus(
    washId: string,
    status: string
  ): Promise<IWashRequest> {
    try {
      const response: AxiosResponse<IWashRequestResponse> =
        await api.patch<IWashRequestResponse>(
          `/admins/washes/${washId}/status`,
          { status }
        );
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.updateWashStatus"));
    }
  }

  /**
   * Get wash history (completed/cancelled washes)
   */
  public async getWashHistory(): Promise<IWashRequest[]> {
    try {
      const response: AxiosResponse<IGetWashRequestsResponse> =
        await api.get<IGetWashRequestsResponse>("/admins/washes/history");
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.fetchWashHistory"));
    }
  }

  /**
   * Get washes by status
   */
  public async getWashesByStatus(status: string): Promise<IWashRequest[]> {
    try {
      const response: AxiosResponse<IGetWashRequestsResponse> =
        await api.get<IGetWashRequestsResponse>(`/admins/washes?status=${status}`);
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, `Failed to fetch ${status} washes`);
    }
  }

  /**
   * Get washes statistics
   */
  public async getWashStats(): Promise<{
    total: number;
    pending: number;
    inProgress: number;
    completed: number;
    cancelled: number;
  }> {
    try {
      const allWashes = await this.getAllWashes();
      return {
        total: allWashes.length,
        pending: allWashes.filter((w) => w.status === "pending").length,
        inProgress: allWashes.filter((w) => w.status === "in_progress").length,
        completed: allWashes.filter((w) => w.status === "completed").length,
        cancelled: allWashes.filter(
          (w) => w.status === "cancelled" || w.status === "not_arrive"
        ).length,
      };
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.fetchWashStatistics"));
    }
  }

  /**
   * Error handling method
   */
  private handleError(error: any, defaultMessage: string): never {
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message || defaultMessage);
    } else {
      throw new Error(i18n.t("serviceErrors.serverError"));
    }
  }
}

const washServiceInstance = new WashService();
export default washServiceInstance;
